import React, { useState } from 'react';

// Price Guarantee Banner Component
const PriceGuaranteeBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);

  const closeBanner = () => {
    setIsVisible(false);
  };

  const toggleTooltip = () => {
    setShowTooltip(!showTooltip);
  };

  const hideTooltip = () => {
    setShowTooltip(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-6 right-6 z-40">
      <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-full shadow-2xl p-8 w-64 h-64 flex items-center justify-center transform transition-all duration-300 hover:scale-105 border-4 border-white relative">
        
        {/* Close Button */}
        <button
          onClick={closeBanner}
          className="absolute top-2 right-2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full w-8 h-8 flex items-center justify-center text-gray-700 hover:text-gray-900 transition-all duration-200 shadow-md"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Info Button */}
        <button
          onClick={toggleTooltip}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={hideTooltip}
          className="absolute bottom-2 right-2 bg-white bg-opacity-60 hover:bg-opacity-80 rounded-full w-6 h-6 flex items-center justify-center text-gray-600 hover:text-gray-800 transition-all duration-200 shadow-sm"
        >
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
          </svg>
        </button>

        {/* Tooltip */}
        {showTooltip && (
          <div className="absolute bottom-0 right-12 bg-black bg-opacity-80 text-white text-xs px-3 py-2 rounded-lg shadow-lg max-w-xs opacity-90 z-50">
            <div className="leading-relaxed">
              <strong className="block mb-1">Rechtlicher Hinweis:</strong>
              Wir behalten uns vor, eingereichte Vergleichsangebote im Einzelfall abzulehnen. Es besteht kein Anspruch auf eine Preisunterbietung oder Auftragserteilung.
            </div>
            {/* Tooltip Arrow */}
            <div className="absolute bottom-2 -right-1 w-2 h-2 bg-black bg-opacity-80 transform rotate-45"></div>
          </div>
        )}
        
        {/* Content centered in circular badge */}
        <div className="text-center text-white px-3">
          {/* Main Text */}
          <div className="space-y-3">
            {/* Eyecatcher Headline */}
            <h2 className="text-lg font-black leading-snug text-shadow-lg">
              Sie haben bereits<br />ein Angebot?
            </h2>
            
            {/* Secondary Text */}
            <p className="text-base font-bold leading-tight">
              Wir unterbieten es!
            </p>
            
            {/* Email Address */}
            <div className="bg-white bg-opacity-90 text-orange-600 rounded-full px-3 py-2 shadow-lg mt-3">
              <p className="font-bold text-sm">
                info@goofy-cocktails.de
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Header Component
const Header = ({ isMenuOpen, setIsMenuOpen, scrollToSection }) => {
  return (
    <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              <div className="logo-wrapper">
                <div 
                  className="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center logo-container"
                  onClick={() => scrollToSection('home')}
                >
                  <img 
                    src="/goofy-cocktails.png"
                    alt="Goofy Cocktails Logo"
                    className="w-full h-full object-cover logo-image"
                  />
                </div>
                {/* 
                  Alternative structure for animated GIF/WebM:
                  <div className="animated-logo">
                    <video autoPlay muted loop playsInline>
                      <source src="/logo-animation.webm" type="video/webm" />
                      <source src="/logo-animation.mp4" type="video/mp4" />
                      <img src="/logo-fallback.png" alt="Goofy Cocktails Logo" />
                    </video>
                  </div>
                  Or for GIF:
                  <div className="animated-logo">
                    <img src="/logo-animated.gif" alt="Goofy Cocktails Logo" />
                  </div>
                */}
              </div>
              <div className="ml-4">
                <h1 className="text-xl font-semibold text-gray-900 leading-tight">Goofy Cocktails</h1>
                <p className="text-sm text-gray-600 leading-tight">Die mobile Cocktailbar</p>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium text-sm">
              Startseite
            </button>
            <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium text-sm">
              Service
            </button>
            <button onClick={() => scrollToSection('gallery')} className="text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium text-sm">
              Galerie
            </button>
            <button onClick={() => scrollToSection('events')} className="text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium text-sm">
              Events
            </button>
            <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium text-sm">
              Über uns
            </button>
            <a 
              href="https://www.instagram.com/goofycocktails" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-amber-600 transition-colors duration-200 p-2"
              title="Folgen Sie uns auf Instagram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.40z"/>
              </svg>
            </a>
            <button onClick={() => scrollToSection('contact')} className="bg-amber-600 text-white px-6 py-3 rounded-lg hover:bg-amber-700 transition-colors duration-200 font-medium text-sm">
              Kontakt
            </button>
          </nav>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-amber-600 transition-colors duration-200 p-2"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t shadow-lg">
          <div className="px-6 py-4 space-y-3">
            <button onClick={() => scrollToSection('home')} className="block w-full text-left py-2 text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium text-sm">
              Startseite
            </button>
            <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium text-sm">
              Service
            </button>
            <button onClick={() => scrollToSection('gallery')} className="block w-full text-left py-2 text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium text-sm">
              Galerie
            </button>
            <button onClick={() => scrollToSection('events')} className="block w-full text-left py-2 text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium text-sm">
              Events
            </button>
            <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium text-sm">
              Über uns
            </button>
            <a 
              href="https://www.instagram.com/goofycocktails" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center py-2 text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium text-sm"
            >
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              Instagram
            </a>
            <div className="pt-3 border-t border-gray-200">
              <button onClick={() => scrollToSection('contact')} className="block w-full text-center bg-amber-600 text-white py-3 rounded-lg font-medium text-sm hover:bg-amber-700 transition-colors duration-200">
                Kontakt
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

// Hero Component
const Hero = ({ scrollToSection }) => {
  return (
    <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1647776112336-72f4c30fafc1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxiYXJ0ZW5kZXJ8ZW58MHx8fHwxNzUyNTA0MTg5fDA&ixlib=rb-4.1.0&q=85"
          alt="Professional bartender making cocktails"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 text-center text-white px-6 lg:px-8 max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-semibold mb-8 leading-tight">
          Wir bringen <span className="text-amber-400">Cocktailgenuss</span>
          <br />
          direkt zu Ihnen
        </h1>
        <p className="text-lg md:text-xl lg:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed font-normal">
          Frische Cocktails, echte Barkeeper und Feierlaune garantiert – direkt bei Ihnen.
          <br />
          <span className="text-amber-400">Ob Firmenfeier, Hochzeit oder Geburtstag.</span>
        </p>
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
          <button 
            onClick={() => scrollToSection('contact')}
            className="bg-amber-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-amber-700 transform hover:scale-105 transition-all duration-200 shadow-lg min-w-[240px]"
          >
            Jetzt Termin vereinbaren
          </button>
          <button 
            onClick={() => scrollToSection('gallery')}
            className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-white hover:text-gray-900 transform hover:scale-105 transition-all duration-200 min-w-[240px]"
          >
            Cocktail-Galerie
          </button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
    </section>
  );
};

// Services Component
const Services = () => {
  const services = [
    {
      title: "Cocktails & Longdrinks",
      description: "Klassiker oder Signature Drinks – gemixt von erfahrenen Barkeepern.",
      icon: "🍹",
      features: ["Frische Zutaten", "Professionelle Barkeeper", "Klassische & moderne Cocktails"]
    },
    {
      title: "Aperitifs",
      description: "Leichte, spritzige Drinks für den stilvollen Empfang.",
      icon: "🥂",
      features: ["Stilvolle Empfänge", "Leichte Drinks", "Elegante Präsentation"]
    },
    {
      title: "Smoothies & Mocktails",
      description: "Alkoholfrei, frisch und fruchtig – ideal für Sommerfeste oder als Alternative.",
      icon: "🍓",
      features: ["100% alkoholfrei", "Frische Früchte", "Für alle Altersgruppen"]
    },
    {
      title: "Komplettes Barcatering",
      description: "Bar, Equipment, Personal – Sie müssen sich um nichts kümmern.",
      icon: "🍸",
      features: ["Vollständige Ausstattung", "Professionelles Personal", "Rundum-Service"]
    }
  ];

  return (
    <section id="services" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-6 leading-tight">
            Unsere <span className="text-amber-600">Services</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-normal">
            Von klassischen Cocktails bis hin zum kompletten Barcatering – wir haben alles für Ihr Event.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <div 
              key={index} 
              id={`service-${service.title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '').replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')}`}
              className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 h-full flex flex-col"
            >
              <div className="text-center flex-1">
                <div className="text-5xl mb-6 leading-none">{service.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 leading-tight">{service.title}</h3>
                <p className="text-gray-600 mb-8 leading-relaxed font-normal flex-1">{service.description}</p>
                <div className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center justify-center text-sm text-gray-700">
                      <svg className="w-4 h-4 text-amber-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="leading-tight font-normal">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Gallery Component
const Gallery = () => {
  const cocktails = [
    // COCKTAILS
    {
      name: "Mojito",
      description: "Limette, Minze, Rohrzucker, weißer Rum, Soda, Lime Juice",
      image: "/mojito.png",
      category: "Cocktails"
    },
    {
      name: "Caipirinha",
      description: "Limette, Rohrzucker, Lime Juice, Pitu",
      image: "/caipirinha.png",
      category: "Cocktails"
    },
    {
      name: "Sex on the Beach",
      description: "Vodka, Triple Sec, Zitronensaft, Cranberrysaft, Orangensaft",
      image: "/sex-on-the-beach.png",
      category: "Cocktails"
    },
    {
      name: "Long Island Iced Tea",
      description: "Gin, Rum, Vodka, Tequila, Zitronensaft, Cola",
      image: "/long-island-iced-tea.png",
      category: "Cocktails"
    },
    {
      name: "Cosmopolitan",
      description: "Vodka, Cointreau, Limettensaft, Cranberrysaft",
      image: "/cosmopolitan.png",
      category: "Cocktails"
    },
    {
      name: "Whiskey Sour",
      description: "Whiskey, Zitronensaft, Zuckersirup",
      image: "/whiskey-sour.png",
      category: "Cocktails"
    },
    {
      name: "Moscow Mule",
      description: "Limette, Vodka, Ginger Beer",
      image: "/moscow-mule.png",
      category: "Cocktails"
    },
    {
      name: "Swimming Pool",
      description: "Rum, Vodka, Blue Curaçao, Cocosirup, Sahne, Ananassaft",
      image: "/swimming-pool.png",
      category: "Cocktails"
    },
    {
      name: "Tequila Sunrise",
      description: "Grenadine, Tequila, Zitronensaft, Orangensaft",
      image: "/tequila-sunrise.png",
      category: "Cocktails"
    },
    {
      name: "Mai Tai",
      description: "Limette, brauner Rum, Cointreau, Mandelsirup, Zuckersirup, Ananassaft",
      image: "/mai-tai.png",
      category: "Cocktails"
    },
    {
      name: "Piña Colada",
      description: "Zitronensaft, Rum, Cocosirup, Sahne, Ananassaft",
      image: "/pina-colada.png",
      category: "Cocktails"
    },
    {
      name: "Old Fashioned",
      description: "Whiskey, Zitronensaft, Zucker, Angostura Bitter",
      image: "/old-fashioned.png",
      category: "Cocktails"
    },
    {
      name: "Planter's Punch",
      description: "Brauner Rum, Orangensaft, Lime Juice, Grenadine",
      image: "/planter's-punch.png",
      category: "Cocktails"
    },
    {
      name: "Sidecar",
      description: "Cognac, Cointreau, Zitronensaft",
      image: "/sidecar.png",
      category: "Cocktails"
    },
    {
      name: "Bloody Mary",
      description: "Vodka, Tomatensaft, Zitronensaft, Worcestersauce, Tabasco, Pfeffer",
      image: "/bloody-marry.png",
      category: "Cocktails"
    },
    {
      name: "Espresso Martini",
      description: "Vodka, Kaffeelikör, Espresso, Zuckersirup",
      image: "/espresso-martini.png",
      category: "Cocktails"
    },

    // APERITIFS
    {
      name: "Aperol Spritz",
      description: "Aperol, Prosecco, Soda, Zitrone",
      image: "/aperol-spritz.png",
      category: "Aperitifs"
    },
    {
      name: "Sarti Spritz",
      description: "Sarti Limoncello, Prosecco, Soda, Zitrone",
      image: "/sarti-spritz.png",
      category: "Aperitifs"
    },
    {
      name: "Lillet Wild Berry",
      description: "Lillet Blanc, Schweppes Russian Wild Berry, Beeren",
      image: "/lillet-wild-berry.png",
      category: "Aperitifs"
    },
    {
      name: "Cuba Libre",
      description: "Limette, Rum, Cola",
      image: "/cuba-libre.png",
      category: "Cocktails"
    },
    {
      name: "Gin Tonic",
      description: "Gin, Tonic Water, Limette",
      image: "/gin-tonic.png",
      category: "Aperitifs"
    },
    {
      name: "Gin Fizz",
      description: "Gin, Lime Juice, Zucker, Soda",
      image: "/gin-fizz.png",
      category: "Cocktails"
    },
    {
      name: "Martini",
      description: "Gin, trockener Wermut, Olive",
      image: "/martini.png",
      category: "Aperitifs"
    },
    {
      name: "Negroni",
      description: "Gin, Campari, roter Wermut",
      image: "/negroni.png",
      category: "Aperitifs"
    },
    {
      name: "Manhattan",
      description: "Whiskey, roter Wermut, Angostura",
      image: "/manhattan.png",
      category: "Aperitifs"
    },

    // MOCKTAILS (alkoholfrei)
    {
      name: "Virgin Mojito",
      description: "Limette, Minze, Rohrzucker, Ginger Ale, Lime Juice",
      image: "/virgin-mojito.png",
      category: "Mocktails"
    },
    {
      name: "Virgin Colada",
      description: "Cocosirup, Ananassaft, Sahne",
      image: "/virgin-colada.png",
      category: "Mocktails"
    },
    {
      name: "Virgin Margarita",
      description: "Limettensaft, Orangensaft, Zitronensaft, Zuckersirup",
      image: "/virgin-margarita.png",
      category: "Mocktails"
    },
    {
      name: "Ipanema",
      description: "Limette, Rohrzucker, Maracujasaft, Ginger Ale",
      image: "/ipanema.png",
      category: "Mocktails"
    },
    {
      name: "Shirley Temple",
      description: "Zitronensaft, Ginger Ale, Lime Juice, Grenadine, Kirsche",
      image: "/shirley-temple.png",
      category: "Mocktails"
    },
    {
      name: "Berry Basil Smash",
      description: "Blaubeeren, Basilikum, Zitronensaft, Zuckersirup, Soda",
      image: "/berry-basil-smash.png",
      category: "Mocktails"
    },

    // SMOOTHIES
    {
      name: "Berry Boost",
      description: "Heidelbeeren, Erdbeeren, Banane, Haferdrink, Agavendicksaft",
      image: "/berry-boost.png",
      category: "Smoothies"
    },
    {
      name: "Mango Maracuja",
      description: "Mango, Maracuja, Apfelsaft, Limettensaft",
      image: "/mango-maracuja.png",
      category: "Smoothies"
    },
    {
      name: "Green Smoothie",
      description: "Ananas, Mango, Babyspinat, Kokoswasser",
      image: "/green-smoothie.png",
      category: "Smoothies"
    },
    {
      name: "Peachy Power",
      description: "Pfirsich, Banane, Orangensaft, Minze",
      image: "/peachy-power.png",
      category: "Smoothies"
    },
    {
      name: "Banana Cacao",
      description: "Banane, Kakaopulver, Datteln, Haferdrink, Erdnussmus",
      image: "/banana-cacao.png",
      category: "Smoothies"
    }
  ];

  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const categories = ['Alle', 'Cocktails', 'Aperitifs', 'Mocktails', 'Smoothies'];

  const filteredCocktails = selectedCategory === 'Alle' 
    ? cocktails 
    : cocktails.filter(cocktail => cocktail.category === selectedCategory);

  return (
    <section id="gallery" className="py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-6 leading-tight">
            Unsere <span className="text-amber-600">Cocktail-Galerie</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-normal">
            Entdecken Sie unsere umfassende Auswahl an professionell zubereiteten Cocktails, Aperitifs, alkoholfreien Mocktails und gesunden Smoothies.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-200 text-sm leading-tight ${
                selectedCategory === category
                  ? 'bg-amber-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-amber-50 hover:text-amber-600 border border-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Cocktail Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {filteredCocktails.map((cocktail, index) => (
            <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="h-64 overflow-hidden">
                <img 
                  src={cocktail.image} 
                  alt={cocktail.name}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-semibold text-gray-900 leading-tight">{cocktail.name}</h3>
                  <span className="text-sm font-medium text-amber-600 bg-amber-50 px-3 py-1 rounded-full leading-tight">
                    {cocktail.category}
                  </span>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed font-normal">{cocktail.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-amber-600 to-yellow-500 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-semibold mb-4 leading-tight">Individuelle Cocktail-Wünsche?</h3>
            <p className="text-amber-100 mb-6 leading-relaxed font-normal max-w-2xl mx-auto">
              Haben Sie einen Lieblings-Cocktail, der nicht in unserer Galerie steht? Kein Problem! 
              Unsere erfahrenen Barkeeper können fast jeden Cocktail zubereiten.
            </p>
            <button 
              onClick={() => {
                document.getElementById('contact')?.scrollIntoView({ 
                  behavior: 'smooth' 
                });
              }}
              className="bg-white text-amber-600 px-8 py-3 rounded-lg font-medium hover:bg-amber-50 transition-colors duration-200 text-sm leading-tight"
            >
              Jetzt anfragen
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Events Component
const Events = () => {
  const events = [
    {
      title: "Firmenfeiern",
      description: "Drinks für das Team, Kunden oder Partner – Professionell & zuverlässig.",
      image: "/firmenfeiern.png",
      features: ["Teambuilding Events", "Kundenfeiern", "Weihnachtsfeiern", "Produktpräsentationen"]
    },
    {
      title: "Hochzeiten",
      description: "Empfang, Party oder Mitternachtssnack – Wir sorgen für Stimmung im Glas.",
      image: "/hochzeiten.png",
      features: ["Sektempfang", "Hochzeitsparty", "Mitternachtsbar", "Brautpaar-Specials"]
    },
    {
      title: "Festivals & öffentliche Events",
      description: "Hohe Kapazität, erfahrenes Personal, reibungsloser Ablauf.",
      image: "/festivals.png",
      features: ["Stadtfeste", "Musikfestivals", "Märkte", "Großveranstaltungen"]
    },
    {
      title: "Geburtstage & private Feiern",
      description: "Zuhause, im Garten oder an einem von Ihnen gewählten Ort – Wir bringen alles mit.",
      image: "/geburtstage.png",
      features: ["Geburtstagsfeiern", "Jubiläen", "Gartenpartys", "Familienfeier"]
    }
  ];

  return (
    <section id="events" className="py-20 bg-gradient-to-br from-amber-50 to-yellow-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Unsere <span className="text-amber-600">Events</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Egal welcher Anlass – wir sorgen für die perfekte Getränkeauswahl und unvergessliche Momente.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {events.map((event, index) => (
            <div 
              key={index} 
              id={`event-${event.title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '').replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')}`}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="h-48 overflow-hidden">
                <img 
                  src={event.image} 
                  alt={event.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{event.title}</h3>
                <p className="text-gray-600 mb-4">{event.description}</p>
                <div className="grid grid-cols-2 gap-2">
                  {event.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center text-sm text-gray-700">
                      <svg className="w-4 h-4 text-amber-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Component
const About = () => {
  const features = [
    {
      title: "Erfahrene Barkeeper",
      description: "Professionell, freundlich, schnell",
      icon: "👨‍🍳"
    },
    {
      title: "Frische Zutaten",
      description: "Alle Drinks werden frisch zubereitet – Auf Wunsch auch alkoholfrei",
      icon: "🍋"
    },
    {
      title: "Komplettservice",
      description: "Sie müssen sich um nichts kümmern",
      icon: "🚛"
    },
    {
      title: "Flexible Angebote",
      description: "Wir passen unser Setup an Ihr Event an",
      icon: "⚙️"
    },
    {
      title: "Klare Kommunikation",
      description: "Schnelle Antworten, faire Preise, transparente Absprachen",
      icon: "💬"
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Über <span className="text-amber-600">Goofy Cocktails</span>
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Wir sind Goofy Cocktails – Die mobile Bar mit Stil, Spaß und hochwertigen Drinks. 
              Ob Hochzeit, Firmenfeier, Geburtstag oder Kinderparty: Wir mixen Cocktails, Longdrinks, 
              Aperitifs, Smoothies und natürlich auch Mocktails. Alles auch alkoholfrei, wenn gewünscht.
            </p>
            <p className="text-lg text-gray-700 mb-8">
              <span className="font-semibold text-amber-600">Hauptsache, es schmeckt</span> – und passt zu Ihrem Event.
            </p>
            
            <div className="space-y-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="text-2xl">{feature.icon}</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-yellow-400 rounded-2xl transform rotate-3"></div>
            <div className="relative bg-white rounded-2xl p-8 shadow-xl">
              <div className="text-center">
                <div className="text-6xl mb-4">🍸</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Deutschlandweit buchbar</h3>
                <p className="text-gray-600 mb-6">
                  Wir bringen Cocktailgenuss direkt zu Ihnen – egal wo in Deutschland Ihr Event stattfindet.
                </p>
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-amber-800 font-semibold">
                    📞 +49 160 90133776
                  </p>
                  <p className="text-amber-700">
                    ✉️ info@goofy-cocktails.de
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Component
const Contact = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission and redirect
    
    try {
      // Submit form data to Formspree via Ajax
      const formData = new FormData(e.target);
      
      // Use environment variable with fallback
      const formspreeUrl = process.env.REACT_APP_FORMSPREE_URL || 'https://formspree.io/f/mqalbzke';
      
      const response = await fetch(formspreeUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        // Show success popup instead of redirecting
        setIsSubmitted(true);
        setShowPopup(true);
        
        // Reset form after successful submission
        e.target.reset();
        
        // Hide popup after 5 seconds
        setTimeout(() => {
          setShowPopup(false);
          setIsSubmitted(false);
        }, 5000);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Fehler beim Senden der Anfrage. Bitte versuchen Sie es erneut oder kontaktieren Sie uns telefonisch.');
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Kontakt aufnehmen</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Lassen Sie uns gemeinsam Ihre Veranstaltung planen. Wir freuen uns auf Ihre Anfrage!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Erreichen Sie uns</h3>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-amber-100 rounded-lg p-3">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Telefon</h4>
                  <p className="text-gray-600">+49 160 90133776</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-amber-100 rounded-lg p-3">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">E-Mail</h4>
                  <p className="text-gray-600">info@goofy-cocktails.de</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-amber-100 rounded-lg p-3">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Standort</h4>
                  <p className="text-gray-600">Deutschlandweit</p>
                </div>
              </div>
            </div>

            <div className="bg-amber-100 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-2">Antwortzeiten</h4>
              <p className="text-gray-600 text-sm">
                Wir antworten in der Regel innerhalb von 24 Stunden auf Ihre Anfrage.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Nachricht senden</h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    name="Name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                    placeholder="Ihr Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail *</label>
                  <input
                    type="email"
                    name="E-Mail"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                    placeholder="ihre@email.de"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                  <input
                    type="tel"
                    name="Telefon"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                    placeholder="Ihre Telefonnummer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event-Typ</label>
                  <select
                    name="Event-Typ"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                  >
                    <option value="">Wählen Sie einen Event-Typ</option>
                    <option value="Hochzeit">Hochzeit</option>
                    <option value="Geburtstag">Geburtstag</option>
                    <option value="Firmenfeier">Firmenfeier</option>
                    <option value="Jubiläum">Jubiläum</option>
                    <option value="Messe">Messe</option>
                    <option value="Sonstiges">Sonstiges</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event-Datum</label>
                  <input
                    type="date"
                    name="Datum"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Anzahl Gäste</label>
                  <input
                    type="text"
                    name="Anzahl der Gäste"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                    placeholder="ca. 50 Personen"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nachricht</label>
                <textarea
                  name="Nachricht"
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                  placeholder="Teilen Sie uns Details zu Ihrem Event mit..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-4 rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                Anfrage senden
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showPopup && isSubmitted && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 transform animate-fade-in">
            <div className="text-center">
              {/* Success Icon */}
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              {/* Success Message */}
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Vielen Dank!
              </h3>
              
              <p className="text-gray-600 mb-8 leading-relaxed">
                Ihre Anfrage wurde erfolgreich übermittelt.
              </p>
              
              {/* Close Button */}
              <button
                onClick={() => setShowPopup(false)}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-3 rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 transition-all duration-200"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center mb-4">
              <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center">
                <img
                  src="/goofy-cocktails.png"
                  alt="Goofy Cocktails Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="ml-3">
                <h3 className="text-xl font-bold">Goofy Cocktails</h3>
                <p className="text-gray-400">Die mobile Cocktailbar</p>
              </div>
            </div>
            <p className="text-gray-400 mb-4">
              Wir bringen Cocktailgenuss direkt zu Ihnen – deutschlandweit für Events jeder Art.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.instagram.com/goofycocktails" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-amber-400 transition-colors"
                title="Folgen Sie uns auf Instagram"
              >
                <span className="sr-only">Instagram</span>
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="mailto:info@goofy-cocktails.de" className="text-gray-400 hover:text-amber-400 transition-colors">
                <span className="sr-only">E-Mail</span>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </a>
            </div>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold mb-4">Services</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="#service-cocktails-longdrinks" className="hover:text-amber-400 transition-colors">Cocktails & Longdrinks</a></li>
              <li><a href="#service-aperitifs" className="hover:text-amber-400 transition-colors">Aperitifs</a></li>
              <li><a href="#service-smoothies-mocktails" className="hover:text-amber-400 transition-colors">Smoothies & Mocktails</a></li>
              <li><a href="#service-komplettes-barcatering" className="hover:text-amber-400 transition-colors">Komplettes Barcatering</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold mb-4">Events</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="#event-firmenfeiern" className="hover:text-amber-400 transition-colors">Firmenfeiern</a></li>
              <li><a href="#event-hochzeiten" className="hover:text-amber-400 transition-colors">Hochzeiten</a></li>
              <li><a href="#event-festivals-oeffentliche-events" className="hover:text-amber-400 transition-colors">Festivals</a></li>
              <li><a href="#event-geburtstage-private-feiern" className="hover:text-amber-400 transition-colors">Private Feiern</a></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; 2020 Goofy Cocktails. Alle Rechte vorbehalten.</p>
        </div>
      </div>
    </footer>
  );
};

export const Components = {
  Header,
  Hero,
  Services,
  Gallery,
  Events,
  About,
  Contact,
  Footer,
  PriceGuaranteeBanner
};